// @ts-strict-ignore
import { Icon, Tooltip as Ds5Tooltip, Typography } from "@fullscript/aviary-web";
import { faExclamationCircle, faInfoCircle } from "@fullscript/pro-regular-svg-icons";
import type { ChangeEvent, FormEvent } from "react";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Avatar, Button, Spacer, Tooltip } from "@aviary";
import { FormFieldInformation } from "@aviary/components/FormFieldInformation";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useCallistoPracOnboardingExperiment } from "@shared/hooks/useCallistoPracOnboardingExperiment/useCallistoPracOnboardingExperiment";
import { useIsRupaRedirectExperience } from "@shared/hooks/useIsRupaRedirectExperience/useIsRupaRedirectExperience";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { useSonicNPIImprovementsExperiment } from "@shared/hooks/useSonicNPIImprovementsExperiment/useSonicNPIImprovementsExperiment";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import type { PractitionerAdditionalAttributesType } from "@shared/types/PractitionerAdditionalAttributesType";
import { StorePlatform } from "@shared/types/StorePlatform";
import { DesignationSearch } from "@unauthenticated/shared/components/DesignationSearch/DesignationSearch";
import { DispensaryNameInput } from "@unauthenticated/shared/components/DispensaryNameInput/DispensaryNameInput";
import { FirstNameInput } from "@unauthenticated/shared/components/FirstNameInput/FirstNameInput";
import { FormWrapper } from "@unauthenticated/shared/components/FormWrapper/FormWrapper";
import { LastNameInput } from "@unauthenticated/shared/components/LastNameInput/LastNameInput";
import { IsNameValid } from "@unauthenticated/shared/components/NameValidation/NameValidation";
import { SSOErrorBox } from "@unauthenticated/shared/components/SSOErrorBox/SSOErrorBox";
import { TermsOfServiceCheckbox } from "@unauthenticated/shared/components/TermsOfServiceCheckbox/TermsOfServiceCheckbox";
import { l } from "@unauthenticated/shared/locales/i18n";

import { RupaRedirectInfoPanel } from "../RupaRedirectInfoPanel/RupaRedirectInfoPanel";
import * as styles from "../SimplifySignUp.styles";
import type { SimplifySignUpProps } from "../types";

interface Props extends SimplifySignUpProps {
  onClick: (e: FormEvent<HTMLFormElement>) => void;
  additionalAttributes?: PractitionerAdditionalAttributesType;
}

type FieldValidationStateType = {
  isFirstNameInvalid: boolean;
  isLastNameInvalid: boolean;
  isDispensaryNameInvalid: boolean;
  isPractitionerTypeInvalid: boolean;
  isTosCheckInvalid: boolean;
};

const CreateAccountPageOne = ({
  errors,
  onClick,
  formData,
  setFormData,
  additionalAttributes,
  ssoErrors,
}: Props) => {
  const { isEmerson } = useSharedGlobalConfig();
  const { t } = useTranslation();
  const { phone, phoneLarge, tablet } = useBreakpoints();
  const isRupaRedirectExperience = useIsRupaRedirectExperience();
  const showInfoPanel = isRupaRedirectExperience && tablet.greaterThan;
  const isSonicNPIImprovementsExperiment = useSonicNPIImprovementsExperiment();

  const [fieldValidationState, setFieldValidationState] = useState<FieldValidationStateType>({
    isFirstNameInvalid: false,
    isLastNameInvalid: false,
    isDispensaryNameInvalid: false,
    isPractitionerTypeInvalid: false,
    isTosCheckInvalid: false,
  });

  const isPeerReferral = !!additionalAttributes?.peerReferralDisplayAttributes;
  const isPracOnboardingEnabled = useCallistoPracOnboardingExperiment() && !isPeerReferral;
  const headerText = isEmerson
    ? t(l.practitionerSignUp.Emerson.CreateAccountHeader)
    : t(l.practitionerSignUp.startPractitionerAccount);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;

    setFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));

    if (errors?.[name]) {
      errors[name] = [];
    }
  };

  const getDefaultDispensaryName = () => {
    const { firstName, lastName } = formData;

    if (!firstName || !lastName) return "";

    const lastChar = lastName[lastName.length - 1];
    const s = lastChar === "s" || lastChar === "S" ? "" : "s";
    const fullName = `${firstName} ${lastName}'${s}`;

    if (isEmerson) {
      return t(l.common.DispensaryNameDefaultWithPlatform, {
        fullName,
        storePlatform: StorePlatform.EMERSON,
      });
    }

    return t(l.common.DispensaryDisplayName, { fullName });
  };

  const handleSetDesignationValue = practitionerType => {
    setFormData(prevState => ({ ...prevState, practitionerType }));
  };

  const onBlurPractitionerType = () => {
    setFieldValidationState(prevState => ({ ...prevState, isPractitionerTypeInvalid: false }));
  };

  const updateTimestamp = tosTimestamp => {
    setFormData(prevState => ({ ...prevState, tosTimestamp }));
  };

  const renderPractitionerTypeError = () => {
    if (fieldValidationState.isPractitionerTypeInvalid) {
      return <FormFieldInformation errors={[t(l.practitionerSignUp.healthProfessionValidation)]} />;
    }

    return null;
  };

  const renderTosError = () => {
    if (fieldValidationState.isTosCheckInvalid && !formData.tosTimestamp) {
      return (
        <div css={styles.tosError}>
          <FontAwesomeIcon icon={faExclamationCircle} css={styles.errorIcon} />
          <Typography customCss={styles.errorText} variant="footnote">
            {t(l.practitionerSignUp.agreeToSValidation)}
          </Typography>
        </div>
      );
    }

    return null;
  };

  const renderHeader = () => {
    if (isPeerReferral) {
      const { referrerFirstName, referrerAvatarUrl } =
        additionalAttributes?.peerReferralDisplayAttributes;

      return (
        <div css={styles.peerReferralTitle}>
          {!!referrerAvatarUrl && (
            <Avatar
              size="small"
              image={referrerAvatarUrl}
              css={styles.userAvatar}
              label={`${referrerFirstName} avatar`}
            />
          )}
          <Typography variant="h3">
            {t(l.practitionerSignUp.peerReferral.signUpTitle, { referrerFirstName })}
          </Typography>
        </div>
      );
    }

    if (isRupaRedirectExperience) {
      return (
        <Typography variant="h3" isMarginless customCss={styles.rupaCreateAccountHeadingToSubtext}>
          {t(l.common.RupaCreateAccountHeading)}
        </Typography>
      );
    }

    return isPracOnboardingEnabled ? (
      <Typography variant="h3">{t(l.practitionerSignUp.PracOnboarding.headerText)}</Typography>
    ) : (
      <Typography variant="h2">{headerText}</Typography>
    );
  };

  const renderNextButton = () => {
    return (
      <Button
        data-e2e="next_button"
        type="submit"
        isFullWidth
        size={isPracOnboardingEnabled ? "large" : "medium"}
      >
        {isPracOnboardingEnabled
          ? t(l.practitionerSignUp.PracOnboarding.nextButtonText)
          : t(l.practitionerSignUp.NextSelectSignUpMethod)}
      </Button>
    );
  };

  const renderFirstLastNameFields = () => {
    const firstNameInput = (
      <FirstNameInput
        value={formData.firstName}
        onChange={handleChange}
        required
        isDirty={fieldValidationState.isFirstNameInvalid}
        isLoading={false}
        errors={errors?.firstName}
      />
    );

    const lastNameInput = (
      <LastNameInput
        value={formData.lastName}
        onChange={handleChange}
        required
        isDirty={fieldValidationState.isLastNameInvalid}
        isLoading={false}
        errors={errors?.lastName}
      />
    );

    const desktopLayout = (
      <div css={styles.row}>
        {firstNameInput}
        <Spacer width="oneHalf" />
        {lastNameInput}
      </div>
    );

    const mobileLayout = (
      <>
        <div css={styles.row}>{firstNameInput}</div>
        <div css={styles.row}>{lastNameInput}</div>
      </>
    );

    return phone.greaterThan ? desktopLayout : mobileLayout;
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let updatedFormData = { ...formData };
    if (formData.dispensaryName === "") {
      updatedFormData = {
        ...updatedFormData,
        dispensaryName: getDefaultDispensaryName(),
      };
      setFormData(updatedFormData);
    }

    const newFieldValidationState: FieldValidationStateType = {
      isFirstNameInvalid: false,
      isLastNameInvalid: false,
      isDispensaryNameInvalid: false,
      isPractitionerTypeInvalid: false,
      isTosCheckInvalid: false,
    };

    const invalidStates: FieldValidationStateType = {
      isFirstNameInvalid: !IsNameValid(updatedFormData.firstName),
      isLastNameInvalid: !IsNameValid(updatedFormData.lastName),
      isDispensaryNameInvalid:
        updatedFormData.dispensaryName.length < 4 || !IsNameValid(updatedFormData.dispensaryName),
      isPractitionerTypeInvalid: isSonicNPIImprovementsExperiment
        ? false
        : !updatedFormData.practitionerType,
      isTosCheckInvalid: !updatedFormData.tosTimestamp,
    };

    Object.entries(invalidStates).forEach(([field, invalid]) => {
      if (invalid) {
        newFieldValidationState[field] = true;
      }
    });
    setFieldValidationState(newFieldValidationState);

    const hasInvalidFields = Object.values(newFieldValidationState).some(Boolean);
    if (!hasInvalidFields) {
      onClick(e);
    }
  };

  const renderSubtitle = () => {
    if (isRupaRedirectExperience) {
      return t(l.common.RupaCreateAccountSubheading);
    }

    if (isPracOnboardingEnabled) {
      return (
        <Trans i18nKey={l.practitionerSignUp.PracOnboarding.TellUsAboutYourself}>
          Tell us a bit about yourself to get started. All accounts are{" "}
          <strong css={styles.freeText}>100% free</strong>
        </Trans>
      );
    }

    return isEmerson ? (
      t(l.practitionerSignUp.Emerson.TellUsAboutYourPractice)
    ) : (
      <Trans i18nKey={l.practitionerSignUp.allAccountsAreFree} components={{ 1: <strong /> }} />
    );
  };

  const renderDesignationSearchTooltip = () => {
    if (isPracOnboardingEnabled && phoneLarge.lessThan) return null;

    const designationTooltipContent = t(l.practitionerSignUp.matchHealthCareCredentials);

    return (
      <Tooltip
        tooltipContent={designationTooltipContent}
        css={styles.tooltip}
        data-testid="designation-search-tooltip"
      >
        <FontAwesomeIcon icon={faInfoCircle} />
      </Tooltip>
    );
  };

  const dispensaryNameTooltipContent = t(l.practitionerSignUp.dispensaryName, {
    patientTermPlural: "patients",
  });

  const renderDispensaryNameField = () => (
    <DispensaryNameInput
      value={formData.dispensaryName}
      onChange={handleChange}
      isDirty={fieldValidationState.isDispensaryNameInvalid}
      placeholder={!isEmerson ? getDefaultDispensaryName() : null}
      required={!isEmerson}
      icon={
        <Ds5Tooltip tooltipContent={dispensaryNameTooltipContent} alignment="left">
          <span css={styles.dispensaryNameTooltipTrigger} aria-label={dispensaryNameTooltipContent}>
            <Icon icon={faInfoCircle} size="small" />
          </span>
        </Ds5Tooltip>
      }
    />
  );

  const formContent = (
    <div css={isRupaRedirectExperience ? styles.rupaRedirectContentBox : styles.contentBox}>
      <div css={!!isPracOnboardingEnabled && styles.headingWrapper}>
        {renderHeader()}
        <Typography isMarginless={isRupaRedirectExperience}>{renderSubtitle()}</Typography>
        <Spacer height="one" />
      </div>

      <SSOErrorBox ssoErrors={ssoErrors} />

      <FormWrapper style={isPracOnboardingEnabled ? { maxWidth: "100%" } : null}>
        <form onSubmit={onSubmit} css={styles.form} noValidate>
          {renderFirstLastNameFields()}
          <div css={styles.row}>{renderDispensaryNameField()}</div>
          {!isSonicNPIImprovementsExperiment && (
            <div css={styles.row}>
              <div css={styles.designationSearchWrapper}>
                <DesignationSearch
                  value={formData.practitionerType}
                  setValue={handleSetDesignationValue}
                  required
                  data-e2e="practitioner-type-trigger"
                  onBlur={() => onBlurPractitionerType()}
                  hasErrors={fieldValidationState.isPractitionerTypeInvalid}
                />
                {renderPractitionerTypeError()}
              </div>
              {renderDesignationSearchTooltip()}
            </div>
          )}

          <Spacer height="half" />
          <TermsOfServiceCheckbox
            updateTimestamp={updateTimestamp}
            isChecked={!!formData.tosTimestamp}
          />
          {renderTosError()}
          <Spacer height="oneHalf" />
          {renderNextButton()}
        </form>
      </FormWrapper>
    </div>
  );

  if (showInfoPanel) {
    return (
      <div css={styles.pageWithInfoPanel}>
        <div css={styles.formContentArea}>{formContent}</div>
        <div css={styles.infoPanelWrapper}>
          <RupaRedirectInfoPanel />
        </div>
      </div>
    );
  }

  if (isRupaRedirectExperience) {
    return <div css={styles.rupaRedirectMobileCenter}>{formContent}</div>;
  }

  return formContent;
};

export { CreateAccountPageOne };
